import React, { useState, useEffect } from 'react';
import { MessageCircle } from 'lucide-react';
import { Navbar } from './components/Navbar';
import { HomePage } from './components/pages/HomePage';
import { ServicesPage } from './components/pages/ServicesPage';
import { BundlesPage } from './components/pages/BundlesPage';
import { CartPage } from './components/pages/CartPage';
import { ChatPage } from './components/pages/ChatPage';
import { SelectedServiceDetails } from './components/BookingModal';
import { AdminBookingsModal } from './components/AdminBookingsModal';
import { AdminPinModal } from './components/AdminPinModal';
import { MyBookingsModal } from './components/MyBookingsModal';
import { PartnerPortalModal } from './components/PartnerPortalModal';
import { MobileAppTabBar, BottomNavTab } from './components/MobileAppTabBar';
import { PWAInstallBanner } from './components/PWAInstallBanner';
import { isStandaloneApp } from './utils/pwaInstall';
import { getServicesList, getBundlesList } from './services/catalogService';
import {
  initHistoryGuard,
  handlePopStateGuard,
  navigateTo,
  AppRoute,
  RouteInfo,
  ROUTE_CHANGE_EVENT
} from './utils/router';
import { LocationPickerModal } from './components/LocationPickerModal';
import {
  loadSavedUserLocation,
  UserLocationState,
  initCustomerLocationOnAppLoad
} from './services/locationService';
import './styles/app.css';

const CART_STORAGE_KEY = 'blishka_cart_items';

function loadStoredCart(): SelectedServiceDetails[] {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const items: SelectedServiceDetails[] = JSON.parse(raw);
    const services = getServicesList();
    return items.map((item) => {
      if (item.durationMinutes) return item;
      const matched = services.find((s) => s.id === item.id);
      return {
        ...item,
        durationMinutes: matched?.durationMinutes || 30
      };
    });
  } catch {
    return [];
  }
}

function saveCart(items: SelectedServiceDetails[]) {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore
  }
}

export const App: React.FC = () => {
  // ── 1. ROUTER STATE WITH BACK-BUTTON HISTORY GUARD ──
  const [initialRouteInfo] = useState<RouteInfo>(() => initHistoryGuard());
  const [currentRoute, setCurrentRoute] = useState<AppRoute>(initialRouteInfo.route);
  const [routeCategory, setRouteCategory] = useState<string | null>(initialRouteInfo.category || null);
  const [routePackageId, setRoutePackageId] = useState<string | null>(initialRouteInfo.packageId || null);

  // ── 2. CART STATE (PERSISTED IN LOCALSTORAGE) ──
  const [selectedItems, setSelectedItems] = useState<SelectedServiceDetails[]>(loadStoredCart);

  useEffect(() => {
    saveCart(selectedItems);
  }, [selectedItems]);

  // ── HYPERLOCAL LOCATION & 5KM SERVICEABILITY STATE ──
  const [userLocation, setUserLocation] = useState<UserLocationState | null>(() => loadSavedUserLocation());
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState<boolean>(false);

  // Auto-detect customer location on app load (asks permission once; auto-detects every subsequent time)
  useEffect(() => {
    initCustomerLocationOnAppLoad((detectedLoc) => {
      setUserLocation(detectedLoc);
    });
  }, []);

  useEffect(() => {
    const handleLocationChange = (e: any) => {
      if (e.detail) {
        setUserLocation(e.detail);
      }
    };
    window.addEventListener('blishka-location-changed', handleLocationChange);
    return () => window.removeEventListener('blishka-location-changed', handleLocationChange);
  }, []);

  // ── 3. ADMIN & PARTNER CONSOLE STATES (Synchronous initialization for instant open) ──
  const isInitialAdmin = initialRouteInfo.route === 'super-admin' || initialRouteInfo.route === 'admin';
  const initialAdminRole: 'super_admin' | 'admin' = initialRouteInfo.route === 'super-admin' ? 'super_admin' : 'admin';
  const isAuthedAdmin = typeof window !== 'undefined' && sessionStorage.getItem('blishka_admin_auth') === 'true';
  const savedAdminPin = typeof window !== 'undefined' ? sessionStorage.getItem('blishka_admin_pin') || '' : '';
  const savedAdminRole = typeof window !== 'undefined' ? (sessionStorage.getItem('blishka_admin_role') as 'super_admin' | 'admin') || initialAdminRole : initialAdminRole;
  const hasFullAdminSession = isAuthedAdmin && Boolean(savedAdminPin) && (savedAdminRole === 'super_admin' || initialRouteInfo.route !== 'super-admin');

  const [isAdminPinModalOpen, setIsAdminPinModalOpen] = useState<boolean>(isInitialAdmin && !hasFullAdminSession);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState<boolean>(isInitialAdmin && hasFullAdminSession);
  const [activeAdminPin, setActiveAdminPin] = useState<string>(hasFullAdminSession ? savedAdminPin : '7994');
  const [adminRole, setAdminRole] = useState<'super_admin' | 'admin'>(savedAdminRole);
  const [requestedAdminRole, setRequestedAdminRole] = useState<'super_admin' | 'admin' | undefined>(isInitialAdmin ? initialAdminRole : undefined);
  const [isPartnerModalOpen, setIsPartnerModalOpen] = useState<boolean>(
    initialRouteInfo.route === 'staff' || initialRouteInfo.route === 'partner'
  );
  const [isMyBookingsModalOpen, setIsMyBookingsModalOpen] = useState<boolean>(
    initialRouteInfo.route === 'my-bookings'
  );

  // Synchronize modal states with routes
  const syncRouteToModalStates = (route: AppRoute) => {
    // Handle Admin routes
    if (route === 'super-admin' || route === 'admin') {
      const targetRole = route === 'super-admin' ? 'super_admin' : 'admin';
      setRequestedAdminRole(targetRole);

      const isAuthed = sessionStorage.getItem('blishka_admin_auth') === 'true';
      const savedPin = sessionStorage.getItem('blishka_admin_pin') || '';
      const savedRole = (sessionStorage.getItem('blishka_admin_role') as 'super_admin' | 'admin') || targetRole;

      if (isAuthed && savedPin && (savedRole === 'super_admin' || route !== 'super-admin')) {
        setActiveAdminPin(savedPin);
        setAdminRole(savedRole);
        setIsAdminModalOpen(true);
        setIsAdminPinModalOpen(false);
      } else {
        setIsAdminPinModalOpen(true);
        setIsAdminModalOpen(false);
      }
    } else {
      setIsAdminModalOpen(false);
      setIsAdminPinModalOpen(false);
    }

    // Handle Staff / Partner route
    if (route === 'staff' || route === 'partner') {
      setIsPartnerModalOpen(true);
    } else {
      setIsPartnerModalOpen(false);
    }

    // Handle Customer My Bookings route
    if (route === 'my-bookings') {
      setIsMyBookingsModalOpen(true);
    } else {
      setIsMyBookingsModalOpen(false);
    }
  };

  // ── 4. ROUTE SYNCHRONIZATION & POPSTATE / HASHCHANGE LISTENER ──
  // Listens to browser Back/Forward buttons, hash changes, and custom router events
  useEffect(() => {
    const handleLocationChange = () => {
      const parsed = handlePopStateGuard();
      setCurrentRoute(parsed.route);
      setRouteCategory(parsed.category || null);
      setRoutePackageId(parsed.packageId || null);
      syncRouteToModalStates(parsed.route);
    };

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);
    window.addEventListener(ROUTE_CHANGE_EVENT, (e: any) => {
      if (e.detail?.route) {
        setCurrentRoute(e.detail.route);
        setRouteCategory(e.detail.category || null);
        setRoutePackageId(e.detail.packageId || null);
        syncRouteToModalStates(e.detail.route);
      } else {
        handleLocationChange();
      }
    });

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleLocationChange);
    };
  }, []);

  // ── 5. ADMIN HANDLERS ──
  const handleAdminPinSuccess = (role: 'super_admin' | 'admin', pin: string) => {
    setActiveAdminPin(pin);
    setAdminRole(role);
    setIsAdminPinModalOpen(false);
    setIsAdminModalOpen(true);
    const targetRoute: AppRoute = role === 'super_admin' ? 'super-admin' : 'admin';
    const targetPath = role === 'super_admin' ? '/super-admin' : '/admin';

    const isNativeAdminDoc =
      typeof window !== 'undefined' &&
      ((window as any).__INITIAL_HTTP_PATHNAME__ || '').includes(targetPath);

    if (!isNativeAdminDoc) {
      window.location.href = `${targetPath}?source=pwa&app=admin`;
      return;
    }

    navigateTo(targetRoute, { replace: true });
  };

  const handleCloseAdminPinModal = () => {
    if (isStandaloneApp() && (currentRoute === 'super-admin' || currentRoute === 'admin')) {
      // In standalone app mode, keep on admin lock screen instead of exiting to customer site
      return;
    }
    setIsAdminPinModalOpen(false);
    navigateTo('/');
  };

  const handleLockAdminModal = () => {
    sessionStorage.removeItem('blishka_admin_auth');
    sessionStorage.removeItem('blishka_admin_pin');
    sessionStorage.removeItem('blishka_admin_role');
    setActiveAdminPin('7994');
    setAdminRole('super_admin');
    setIsAdminModalOpen(false);
    if (isStandaloneApp() && (currentRoute === 'super-admin' || currentRoute === 'admin')) {
      // In standalone app mode, return to PIN unlock screen
      setIsAdminPinModalOpen(true);
      return;
    }
    setIsAdminPinModalOpen(false);
    navigateTo('/');
  };

  const handleClosePartnerModal = () => {
    setIsPartnerModalOpen(false);
    navigateTo('/');
  };

  const handleCloseMyBookingsModal = () => {
    setIsMyBookingsModalOpen(false);
    navigateTo('/');
  };

  // ── 6. CART MANAGEMENT ──
  const handleToggleService = (id: string, name: string, price: number) => {
    setSelectedItems((prev) => {
      const exists = prev.some((item) => item.id === id);
      if (exists) {
        return prev.filter((item) => item.id !== id);
      } else {
        const services = getServicesList();
        const bundles = getBundlesList();
        const serviceObj = services.find((s) => s.id === id);
        const bundleObj = bundles.find((b) => b.id === id);
        const originalPrice = serviceObj?.originalPrice || bundleObj?.originalValue || Math.round(price * 1.35);
        const category = serviceObj?.category || (bundleObj ? 'Curated Bundle' : 'Salon Care');
        const durationMinutes = serviceObj?.durationMinutes || (bundleObj ? (bundleObj.servicesIncluded?.length ? bundleObj.servicesIncluded.length * 25 : 60) : 30);

        return [
          ...prev,
          {
            id,
            name: serviceObj?.name || bundleObj?.name || name,
            price,
            originalPrice,
            category,
            durationMinutes
          }
        ];
      }
    });
  };

  const handleRemoveItem = (id: string) => {
    setSelectedItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleClearCart = () => {
    setSelectedItems([]);
  };

  // Determine active tab for persistent bottom tab bar
  const activeBottomTab: BottomNavTab =
    currentRoute === 'services'
      ? 'services'
      : currentRoute === 'bundles'
      ? 'bundles'
      : currentRoute === 'cart'
      ? 'cart'
      : currentRoute === 'chat'
      ? 'chat'
      : 'home';

  return (
    <div className="app-container">
      {/* PWA App Install Prompt Banner */}
      <PWAInstallBanner />

      {/* Delicate floral ambient gradient */}
      <div className="ambient-decor" />

      <div className="content-wrapper">
        {/* Navigation with live cart badge, mobile drawer & location pill */}
        <Navbar
          onOpenBookingModal={() => navigateTo('/cart')}
          onOpenMyBookings={() => navigateTo('/my-bookings')}
          cartCount={selectedItems.length}
          currentRoute={currentRoute}
          userLocation={userLocation}
          onOpenLocationPicker={() => setIsLocationPickerOpen(true)}
        />

        {/* Short Red Content when customer area is out of deployed area (Amazon / Flipkart style) */}
        {userLocation?.status === 'unserviceable' &&
          currentRoute !== 'admin' &&
          currentRoute !== 'super-admin' &&
          currentRoute !== 'staff' &&
          currentRoute !== 'partner' && (
            <div className="location-out-of-area-strip">
              <div className="location-out-of-area-inner">
                <span className="loc-strip-dot">🔴</span>
                <span className="loc-strip-text">
                  Doorstep parlour care in your Area is Coming Soon!
                </span>
                <button
                  type="button"
                  onClick={() => setIsLocationPickerOpen(true)}
                  className="loc-strip-change-btn"
                >
                  Change Location
                </button>
              </div>
            </div>
          )}

        {/* 1. HOME PAGE (/) */}
        {currentRoute === 'home' && (
          <HomePage
            selectedItems={selectedItems}
            onToggleService={handleToggleService}
            activeMenuCategory={null}
          />
        )}

        {/* 2. SERVICES PAGE (/services) */}
        {currentRoute === 'services' && (
          <ServicesPage
            selectedItems={selectedItems}
            onToggleService={handleToggleService}
            initialCategory={routeCategory}
          />
        )}

        {/* 3. BUNDLES PAGE (/bundles) */}
        {currentRoute === 'bundles' && (
          <BundlesPage
            selectedItems={selectedItems}
            onToggleService={handleToggleService}
            selectedPackageId={routePackageId}
          />
        )}

        {/* 4. CART & BOOKING PAGE (/cart) */}
        {currentRoute === 'cart' && (
          <CartPage
            selectedItems={selectedItems}
            onRemoveItem={handleRemoveItem}
            onAddItem={(item) => handleToggleService(item.id, item.name, item.price)}
            onClearCart={handleClearCart}
          />
        )}

        {/* 5. AI CHATBOT PAGE (/chat) */}
        {currentRoute === 'chat' && (
          <ChatPage
            onOpenBookingModal={() => navigateTo('/cart')}
            onNavigateToMenu={() => navigateTo('/services')}
          />
        )}
      </div>

      {/* Floating Desktop Chat Launcher (Hidden when on chat, cart, or consoles) */}
      {currentRoute !== 'chat' &&
        currentRoute !== 'cart' &&
        !isAdminModalOpen &&
        !isPartnerModalOpen &&
        !isAdminPinModalOpen && (
          <button
            type="button"
            className="desktop-chat-launcher"
            onClick={() => navigateTo('/chat')}
            aria-label="Open Blishka Assistant Chatbot"
          >
            <div className="desktop-chat-icon-wrap">
              <MessageCircle size={22} />
              <span className="desktop-chat-pulse" />
            </div>
            <div className="desktop-chat-label-wrap">
              <span className="desktop-chat-title">Chat &amp; Book</span>
              <span className="desktop-chat-sub">EN / हिंदी</span>
            </div>
          </button>
        )}

      {/* Secret Admin Authentication PIN Modal */}
      <AdminPinModal
        isOpen={isAdminPinModalOpen}
        onClose={handleCloseAdminPinModal}
        onSuccess={handleAdminPinSuccess}
        requestedRole={requestedAdminRole}
      />

      {/* Admin Bookings & Excel Management Modal (Role-Based: Super Admin / Operations Admin) */}
      <AdminBookingsModal
        isOpen={isAdminModalOpen}
        adminPin={activeAdminPin}
        adminRole={adminRole}
        onSwitchRole={(newRole) => setAdminRole(newRole)}
        onClose={handleLockAdminModal}
        onLockOut={handleLockAdminModal}
      />

      {/* Staff / Beautician Partner Portal (#staff or #partner) */}
      <PartnerPortalModal
        isOpen={isPartnerModalOpen}
        onClose={handleClosePartnerModal}
      />

      {/* Customer My Bookings Modal (Track bookings with mobile OTP login) */}
      <MyBookingsModal
        isOpen={isMyBookingsModalOpen}
        onClose={handleCloseMyBookingsModal}
        onNavigateToServices={() => navigateTo('/services')}
      />

      {/* Hyperlocal 5 km Location Picker Modal */}
      <LocationPickerModal
        isOpen={isLocationPickerOpen}
        onClose={() => setIsLocationPickerOpen(false)}
        onLocationSelected={(loc) => {
          setUserLocation(loc);
        }}
        currentLocation={userLocation}
      />

      {/* Persistent Bottom Tab Bar (Hidden completely when Admin or Staff Console is open) */}
      {!isAdminModalOpen && !isPartnerModalOpen && !isAdminPinModalOpen && (
        <MobileAppTabBar
          cartCount={selectedItems.length}
          onSelectHomeTab={() => navigateTo('/')}
          onSelectServicesTab={() => navigateTo('/services')}
          onSelectBundlesTab={() => navigateTo('/bundles')}
          onOpenCart={() => navigateTo('/cart')}
          onOpenChat={() => navigateTo('/chat')}
          activeTab={activeBottomTab}
        />
      )}
    </div>
  );
};

export default App;
